<template>
  <v-form ref="form" v-model="valid" @submit.prevent="doSignin">
    <v-card flat>
      <v-card-title class="headline">HawkSoft Agent Portal</v-card-title>
      <v-card-text>
        <v-row row wrap>
          <v-col>
            <v-text-field
              id="username"
              v-model="username"
              :rules="uRules"
              validate-on-blur
              label="Username"
              placeholder="Enter your username"
              autocomplete="username"
              maxlength="12">
                <template v-slot:append>
                  <v-btn icon @click="showUsernameHelp=!showUsernameHelp">
                    <v-icon>mdi-help-circle-outline</v-icon>
                  </v-btn>
                </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row wrap>
          <v-col>
            <v-text-field id="password" v-model="password" :rules="passwordRules" validate-on-blur label="Password" placeholder="Enter Password" type="password" autocomplete="current-password" />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn :disabled="!onCancel" color="blue lighten-1" text @click="cancel">Cancel</v-btn>
        <v-spacer />
        <v-btn color="" text type="submit">Sign In</v-btn>
      </v-card-actions>
      <v-alert :value="displayError" type="error">{{ error }}</v-alert>
    </v-card>

    <v-dialog v-model="showUsernameHelp" max-width="300px">
      <v-card>
        <v-card-title class="integrity white--text">
          Whats my username?
        </v-card-title>
        <v-card-subtitle class="integrity gray4--text" />
        <v-card-text>
          <v-row wrap>
            <v-col cols="12">
              <div>
                Your username consists of your Agency ID and Initials.
              </div>
              <div class="pt-1">
                It can be entered in two ways:
              </div>
            </v-col>
            <v-col cols="12" class="py-0">
              <ol>
                <li>
                  Initials@Agency ID
                  <div>Ex. ABC@123</div>
                </li>
                <li>
                  Agency ID\Initials
                  <div>Ex. 123\ABC</div>
                </li>
              </ol>
            </v-col>
            <div class="pt-1">
              Initials cannot have spaces or any of the following characters:
              <span class="font-weight-bold">
                / \ : * ? &#39; &quot; &lt; &gt; |
              </span>
            </div>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="success" @click="showUsernameHelp = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>
<style>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
}
</style>
<script>
import { mapActions } from 'vuex';

import analyticsService from '@/services/shared/analyticsService.js';

const lastAmpersatLookahead = /@(?=[^@]+$)/;
const containsBackslash = /\\/;

export default {
  props: {
    showSignin: {
      type: Boolean,
      default: false
    },
    onCancel: {
      type: Function,
      default: null
    }
  },
  data () {
    return {
      valid: false,
      usernameValue: '',
      password: '',
      loading: false,
      error: '',
      displayError: false,
      showUsernameHelp: false,
      uRules: [
        v => this.usernameRules(v)
      ],
      passwordRules: [
        v => !!v || 'Password is required.',
        v => v !== '' || 'Password is required.'
      ]
    };
  },
  computed: {
    username: {
      get: function () {
        return this.usernameValue;
      },
      set: function (value) {
        this.usernameValue = value.toUpperCase();
      }
    }
  },
  methods: {
    ...mapActions('auth', ['login']),
    getInitials () {
      if (this.username.lastIndexOf('@') === 3 && !containsBackslash.test(this.username)) {
        var inputs = this.username.split(lastAmpersatLookahead);
        if (inputs.length > 1) {
          return inputs[0];
        }
      } else if (this.username.indexOf('\\') >= 0) {
        inputs = this.username.split('\\');

        if (inputs.length > 0 || inputs.length < 2) {
          return inputs[1];
        }
      }

      return null;
    },
    getAgencyId () {
      if (this.username.lastIndexOf('@') === 3 && !containsBackslash.test(this.username)) {
        var inputs = this.username.split(lastAmpersatLookahead);
        if (inputs.length > 1) {
          return inputs[1];
        }
      } else if (this.username.indexOf('\\') >= 0) {
        inputs = this.username.split('\\');
        if (inputs.length > 0) {
          return inputs[0];
        }
      }

      return null;
    },
    usernameRules: function (v) {
      if (!v) return 'Username is required.';

      // regex `illegalChars` is a negated set; a pass constitutes as `true`
      const illegalChars = /^[^\\:*'"<>| /]+$/;
      const numberRegex = /^\d+$/;

      let inputs = v.split(lastAmpersatLookahead);

      if (v.lastIndexOf('@') === 3 && !containsBackslash.test(v)) {
        if (inputs.length > 1) {
          if (!illegalChars.test(inputs[0])) return 'Initials cannot contain illegal characters.';
          // parseInt does not catch special chars; regex eval first
          if (!numberRegex.test(inputs[1])) return 'Agency ID must include only numbers.';

          var asInt = parseInt(inputs[1], 10); // specify radix; parseInt does not default to decimal
          if (asInt <= 0) return 'Agency ID must be greater than 0.'; //
          if (asInt > 99999999) return 'Agency ID can only contain up to 8 numbers.';
        } else if ((inputs[1] || null) === null) return 'Agency ID is required.';
      } else if (v.indexOf('\\') >= 0) {
        inputs = v.split('\\');

        if (inputs.length > 2) return 'Please follow the format in the hint.';

        if (inputs.length > 1) {
          if ((inputs[0] || null) === null) return 'Agency ID is required.';
          if (!numberRegex.test(inputs[0])) return 'Agency ID must include only numbers.';

          asInt = parseInt(inputs[0], 10);
          if (asInt <= 0) return 'Agency ID must be greater than 0.';
          if (asInt > 99999999) return 'Agency ID can only contain up to 8 numbers.';

          if ((inputs[1] || null) === null) return 'User initials are required.';
          if (inputs[1].length < 3) return 'Initials must contain 3 characters.';
          if (inputs[1].length > 3) return 'Initials can only contain 3 characters.';
          if (!illegalChars.test(inputs[1])) return 'Initials cannot contain illegal characters.';
        }
      } else {
        if (inputs.length < 2) return 'Must include initials and agency ID.';
        if ((inputs[0] || null) === null) return 'User initials are required.';
        if (inputs[0].length < 3) return 'Initials must contain 3 characters.';
        if (inputs[0].length > 3) return 'Initials can only contain 3 characters.';
        return 'Must include properly formatted initials and agency ID.';
      }

      return true;
    },
    async doSignin  () {
      var isValid = this.$refs.form.validate();
      if (!isValid) return;

      if (this.loading) return;
      this.loading = true;

      var agencyId = this.getAgencyId();
      if (agencyId == null) {
        // Shouldnt get here if this.valid is true, but I want to be extra safe.
        this.error = 'Agency ID must be provided.';
        return;
      }

      var initials = this.getInitials();
      if (initials == null) {
        // Shouldnt get here if this.valid is true, but I want to be extra safe.
        this.error = 'Initials must be provided.';
        return;
      }

      analyticsService.sendEventFromUser(this.$gtag, 'SignOn', 'Nav', 'UserSignOn', agencyId, initials);

      this.login({
        initials,
        agencyId,
        password: this.password
      })
        .then(() => {
          this.$router.push({ path: '/' });
        })
        .catch((err) => {
          this.displayError = true;
          this.error = err.message || 'An error occurred during login';
          this.$nextTick(() => {
            this.resetForm();
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cancel () {
      if (this.onCancel) this.onCancel();
    },
    resetForm () {
      this.$refs.form.resetValidation();
      this.username = '';
      this.password = '';
    }
  }
};
</script>
